import { useEffect, useMemo, useState } from "react";
import { apiGet } from "../lib/api";

const DAILY_ENDPOINT = "/daily/questions";

/**
 * useDailyQuiz — fetches today's 3 questions, manages user progress
 * through them, and exposes a small state machine.
 *
 * State:
 *   phase: "loading" | "playing" | "done"
 *   error: string | null
 *   daily: { date, questions[] } | null
 *   idx: number
 *   selected: number | null
 *   revealed: boolean
 *   score: number (memoized correct count)
 *
 * Actions:
 *   setSelected(i)
 *   verify()  — reveals current question
 *   next()    — advances or transitions to "done"
 */
export default function useDailyQuiz() {
  const [daily, setDaily] = useState(null);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState(null);

  useEffect(() => {
    apiGet(DAILY_ENDPOINT)
      .then((d) => {
        if (!d?.questions || d.questions.length === 0) {
          setError("Domande non disponibili al momento.");
          return;
        }
        setDaily(d);
        setPhase("playing");
      })
      .catch(() => setError("Impossibile caricare le domande del giorno."));
  }, []);

  const score = useMemo(() => answers.filter((a) => a.correct).length, [answers]);

  const verify = () => {
    if (selected === null || revealed || !daily) return;
    const q = daily.questions[idx];
    setAnswers((a) => [...a, { qid: q.question_id, correct: selected === q.correct_index }]);
    setRevealed(true);
  };

  const next = () => {
    setSelected(null);
    setRevealed(false);
    if (idx + 1 >= (daily?.questions?.length || 0)) {
      setPhase("done");
    } else {
      setIdx((i) => i + 1);
    }
  };

  return {
    phase, error, daily, idx, selected, revealed, score,
    setSelected, verify, next,
  };
}
